import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ReactFlowProvider } from '@xyflow/react'
import { CanvasPane } from './CanvasPane'
// Keep ReactFlowProvider wrapping the whole page so ref-jumps work.
import { useGraphStore } from '../stores/useGraphStore'
import { getPublicDocumentBySlug, type GraphDocument } from '../lib/documents'

export function PublicViewer() {
  const { slug } = useParams<{ slug: string }>()
  const [doc, setDoc] = useState<GraphDocument | null>(null)
  const [error, setError] = useState<string | null>(null)

  const hydrate = useGraphStore((s) => s.hydrateFromServer)
  const reset = useGraphStore((s) => s.reset)

  useEffect(() => {
    if (!slug) return
    let active = true
    ;(async () => {
      try {
        const d = await getPublicDocumentBySlug(slug)
        if (!active) return
        if (!d) {
          setError('Document not found or not public')
          return
        }
        setDoc(d)
        hydrate(d.id, d.content, { readOnly: true })
      } catch (e) {
        if (active) setError((e as Error).message)
      }
    })()
    return () => {
      active = false
      reset()
    }
  }, [slug, hydrate, reset])

  if (error) {
    return (
      <div className="h-dvh w-full flex items-center justify-center bg-background">
        <p
          className="text-[14px] font-light text-destructive"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          {error}
        </p>
      </div>
    )
  }
  if (!doc) {
    return (
      <div className="h-dvh w-full flex items-center justify-center bg-background">
        <div className="w-5 h-5 border border-foreground/20 border-t-foreground rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <ReactFlowProvider>
    <div className="h-dvh w-full flex flex-col bg-background overflow-hidden">
      <header className="flex-shrink-0 h-12 border-b border-border flex items-center justify-between px-6">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="text-[13px] font-medium tracking-tight truncate"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            {doc.title}
          </span>
          <span
            className="text-[10px] uppercase tracking-wider text-muted-foreground font-light"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            Read-only
          </span>
        </div>
        <a
          href="/graph"
          className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          JSON Graph
        </a>
      </header>
      <div className="flex-1 overflow-hidden">
        <CanvasPane />
      </div>
    </div>
    </ReactFlowProvider>
  )
}
